import React, { useMemo, useState } from 'react';
import { Drawer } from 'antd';
import { useTypedSelector } from '@hooks';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { setToggleOpenTranscript } from '@redux/actions/mediaViewerAction';
import { useOrganization } from '@components/OrganizationBoundary';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';

import { parseSegments } from '@pages/MediaViewer/components/TranscriptDrawer/components/TranscriptionContent/utils';

import HeadTitle from './components/HeadTitle';
import CreateTranscript from './components/CreateTranscript';
import TranscriptionLoader from './components/TranscriptionLoader';
import TranscriptionContent from './components/TranscriptionContent';

import './TranscriptDrawer.less';

type TranscriptDrawerProps = {
  isOpenTranscript: boolean;
};

function TranscriptDrawer({ isOpenTranscript }: TranscriptDrawerProps) {
  const dispatch = useDispatch();
  const { currentOrganization } = useOrganization(false);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const metadata = selectedVersion?.metadata;
  const transcriptionStatus =
    selectedVersion?.metadata && 'transcription' in selectedVersion.metadata
      ? selectedVersion.metadata.transcription.status
      : null;

  const canModifyTranscription =
    !!currentOrganization?.entity?.features.audioTranscription &&
    !!asset?.asset?.permissions.transcribe;

  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  const availableTranscripts = useMemo(() => {
    if (transcriptionStatus !== 'ready') return null;
    if (metadata?.type !== 'video' && metadata?.type !== 'audio') return null;
    const _items: Record<string, string> = {};
    if (metadata?.transcription?.url) {
      _items.url = metadata.transcription.url;
    }
    if (metadata?.transcription?.subRipUrl) {
      _items.subRipUrl = metadata.transcription.subRipUrl;
    }
    if (metadata?.transcription?.webVttUrl) {
      _items.webVttUrl = metadata.transcription.webVttUrl;
    }
    return Object.values(_items).length ? _items : null;
  }, [metadata, transcriptionStatus]);

  const fileUrl = availableTranscripts?.url;

  const transcriptCacheKey = useMemo(() => {
    if (!fileUrl) return null;
    try {
      return new URL(fileUrl).pathname;
    } catch {
      return fileUrl;
    }
  }, [fileUrl]);

  const { data: transcriptData, isLoading: isTranscriptLoading } = useQuery({
    queryKey: ['transcript', transcriptCacheKey],
    enabled: !!fileUrl,
    queryFn: async () => {
      if (!fileUrl) return [];
      const res = await fetch(fileUrl);
      if (!res.ok) throw new Error('Failed to fetch');
      return parseSegments(await res.text());
    }
  });

  return (
    <Drawer
      title={
        <HeadTitle
          canModifyTranscription={canModifyTranscription}
          hasTranscript={!!transcriptData?.length}
          isAutoScrolling={isAutoScrolling}
          setIsAutoScrolling={setIsAutoScrolling}
          availableTranscripts={availableTranscripts}
        />
      }
      zIndex={100}
      className="media_viewer_transcript-drawer"
      closeIcon={<CrossSvg />}
      placement="right"
      onClose={() => dispatch(setToggleOpenTranscript(false))}
      width={432}
      destroyOnClose={true}
      open={isOpenTranscript}
      afterOpenChange={(open) =>
        !open && dispatch(setToggleOpenTranscript(null))
      }
    >
      {(transcriptionStatus === 'skipped' ||
        transcriptionStatus === 'error') && <CreateTranscript />}
      {transcriptionStatus === 'pending' && <TranscriptionLoader />}
      {transcriptionStatus === 'ready' && availableTranscripts && (
        <TranscriptionContent
          transcriptData={transcriptData || []}
          isTranscriptLoading={isTranscriptLoading}
          isAutoScrolling={isAutoScrolling}
          setIsAutoScrolling={setIsAutoScrolling}
        />
      )}
    </Drawer>
  );
}

export default TranscriptDrawer;
